import { messageIsActive, torontoDate } from './jd-announcement-schedule.js';

(function () {
    class SlideshowHero {
      constructor(root) {
        this.root = root;
        this.root._slideshowInstance = this;
        this.track = root.querySelector('.slideshow-hero__track');
        this.allSlides = Array.from(root.querySelectorAll('.slideshow-hero__slide'));
        this.allDots = Array.from(root.querySelectorAll('.slideshow-hero__dot'));
        this.slides = [];
        this.dots = [];
        this.prevBtn = root.querySelector('.slideshow-hero__arrow--prev');
        this.nextBtn = root.querySelector('.slideshow-hero__arrow--next');
        this.interval = parseInt(root.dataset.autoplayInterval, 10) || 5000;
        this.index = 0;
        this.timer = null;
        this.suppressClick = false;

        this.bindSlideLinks();
        this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));
        this.allDots.forEach((dot, i) => dot.addEventListener('click', () => {
          const activeIndex = this.slides.indexOf(this.allSlides[i]);
          if (activeIndex >= 0) this.go(activeIndex);
        }));
        root.addEventListener('mouseenter', () => this.stop());
        root.addEventListener('mouseleave', () => this.start());
        this.bindTouchSwipe();
        this.bindVideoAdvance();
        this.refreshSchedule = this.refreshSchedule.bind(this);
        this.refreshSchedule();
        this.scheduleTimer = setInterval(this.refreshSchedule, 30000);
        this.onVisible = () => { if (!document.hidden) this.refreshSchedule(); };
        document.addEventListener('visibilitychange', this.onVisible);
        window.addEventListener('pageshow', this.refreshSchedule);
      }

      refreshSchedule() {
        const today = torontoDate();
        const activeSlides = this.allSlides.filter(slide => messageIsActive({
          enabled: slide.dataset.enabled !== 'false',
          start: slide.dataset.start || '',
          end: slide.dataset.end || ''
        }, today, this.root.dataset.previewAll === 'true'));
        if (this.ready && activeSlides.length === this.slides.length &&
            activeSlides.every((slide, i) => slide === this.slides[i])) return;
        const current = this.slides[this.index];
        this.stop();
        this.slides = activeSlides;
        this.dots = this.allDots.filter((dot, i) => activeSlides.includes(this.allSlides[i]));
        this.allSlides.forEach(slide => {
          slide.hidden = !activeSlides.includes(slide);
          slide.setAttribute('aria-hidden', 'true');
          if (slide.dataset.slideLink) slide.tabIndex = -1;
          const video = slide.querySelector('video');
          if (video) {
            video.loop = !slide.hidden && activeSlides.length === 1;
            if (slide.hidden) video.pause();
          }
        });
        this.allDots.forEach((dot, i) => {
          dot.hidden = !activeSlides.includes(this.allSlides[i]);
          dot.removeAttribute('aria-current');
        });
        [this.prevBtn, this.nextBtn, this.root.querySelector('.slideshow-hero__dots')]
          .filter(Boolean).forEach(control => { control.hidden = activeSlides.length < 2; });
        this.root.parentElement.hidden = activeSlides.length === 0;
        this.root.dataset.scheduleReady = 'true';
        this.ready = true;
        this.go(Math.max(0, activeSlides.indexOf(current)), true);
      }

      destroy() {
        this.stop();
        clearInterval(this.scheduleTimer);
        document.removeEventListener('visibilitychange', this.onVisible);
        window.removeEventListener('pageshow', this.refreshSchedule);
        this.allSlides.forEach(slide => slide.querySelector('video')?.pause());
        delete this.root._slideshowInstance;
      }

      go(newIndex, force = false) {
        if (!this.slides.length || (!force && this.slides.length < 2)) return;
        this.index = (newIndex + this.slides.length) % this.slides.length;
        this.track.style.transform = `translateX(-${this.index * 100}%)`;

        this.slides.forEach((slide, i) => {
          slide.setAttribute('aria-hidden', i !== this.index);
          if (slide.dataset.slideLink) {
            slide.tabIndex = i === this.index ? 0 : -1;
          }
          const video = slide.querySelector('video');
          if (video) {
            if (i === this.index) {
              video.currentTime = 0;
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }
        });

        this.dots.forEach((dot, i) => {
          if (i === this.index) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        this.restart();
      }

      bindSlideLinks() {
        this.allSlides.forEach((slide, i) => {
          const link = slide.dataset.slideLink;
          if (!link) return;

          slide.tabIndex = -1;

          slide.addEventListener('click', (event) => {
            if (this.suppressClick) {
              this.suppressClick = false;
              return;
            }
            if (event.target.closest('a, button')) return;
            window.location.href = link;
          });

          slide.addEventListener('keydown', (event) => {
            if (event.target.closest('a, button')) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              window.location.href = link;
            }
          });
        });
      }

      bindTouchSwipe() {
        let touchStartX = 0;
        let touchEndX = 0;

        this.track.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
          this.suppressClick = false;
        }, { passive: true });

        this.track.addEventListener('touchend', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          const diff = touchStartX - touchEndX;

          if (Math.abs(diff) > 10) {
            this.suppressClick = true;
          }

          if (Math.abs(diff) > 50) {
            if (diff > 0) this.go(this.index + 1);
            else this.go(this.index - 1);
          }
        }, { passive: true });
      }

      bindVideoAdvance() {
        this.allSlides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (video) {
            video.addEventListener('ended', () => {
              if (slide === this.slides[this.index]) this.go(this.index + 1);
            });
          }
        });
      }

      currentSlideIsVideo() {
        return this.slides[this.index].dataset.mediaType === 'video_slide';
      }

      start() {
        this.stop();
        if (this.slides.length < 2) return;
        if (!this.currentSlideIsVideo()) {
          this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
        }
      }

      stop() {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
      }

      restart() {
        this.stop();
        this.start();
      }
    }

    const initSlideshows = () => {
      document.querySelectorAll('.slideshow-hero').forEach((root) => {
        if (!root._slideshowInstance) {
          new SlideshowHero(root);
        }
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSlideshows);
    } else {
      initSlideshows();
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', initSlideshows);
      document.addEventListener('shopify:section:unload', event => {
        event.target.querySelectorAll('.slideshow-hero').forEach(root => root._slideshowInstance?.destroy());
      });
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('.slideshow-hero__slide');
        if (!slide) return;
        const root = slide.closest('.slideshow-hero');
        if (root && root._slideshowInstance) {
          const index = root._slideshowInstance.slides.indexOf(slide);
          if (index < 0) return;
          root._slideshowInstance.go(index, true);
          root._slideshowInstance.stop();
        }
      });

      document.addEventListener('shopify:block:deselect', (event) => {
        const root = event.target.closest('.slideshow-hero');
        if (root && root._slideshowInstance) {
          root._slideshowInstance.start();
        }
      });
    }
  })();
